import { useState } from 'react';
import clsx from 'clsx';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '~/shared/components/buttons/Button';
import {
	getCategoriesOptions,
	InterventionDefinitionTypeEnum,
	type GoalAndInterventionDefinitionLibraryViewModel,
	type InterventionDefinitionViewModel
} from '@generated';
import { SearchInput } from '~/shared/forms/controls/base-controls/text-controls/SearchInput';
import { Section } from '~/shared/section/Section';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useGoalsAndInterventionsWizard } from '../GoalsAndInterventionsWizardContext';
import { useGetCategoryLabel } from '~/libraries/categories/useGetCategoryLabel';
import { ButtonGroupField } from '~/shared/forms/controls/base-fields/ButtonGroupField';
import { DataTablePager } from '~/shared/data-tables/DataTablePager';
import { InterventionCard } from '../../interventions/InterventionCard';
import type { SelectedWizardIntervention } from '../reducer/wizardReducer';

function filterInterventions(
	goalAndInterventions: GoalAndInterventionDefinitionLibraryViewModel,
	existingInterventionIds: string[],
	selectedInterventions: SelectedWizardIntervention[],
	selectedCategoryNames: string[],
	disciplineFilter: number,
	searchText: string
): InterventionDefinitionViewModel[] {
	const selectedInterventionProcedureConceptIds = selectedInterventions
		.filter(x => x.type === 'predefined')
		.map(x => x.interventionDefinition.procedureConceptId)
		.filter(x => x !== null) as string[];
	const lowerSearchCase = searchText.toLocaleLowerCase();
	return goalAndInterventions.interventionDefinitions.filter(x => {
		if (existingInterventionIds.some(y => y === x.id) || selectedInterventionProcedureConceptIds.some(y => y === x.procedureConceptId)) {
			return false;
		} else if (
			searchText.length >= 3 &&
			!x.title.toLocaleLowerCase().includes(lowerSearchCase) &&
			!x.definitionText.toLocaleLowerCase().includes(lowerSearchCase)
		) {
			return false;
		} else if (selectedCategoryNames.length > 0 && !selectedCategoryNames.includes(x.category)) {
			return false;
		} else if (!x.disciplineIds.includes(disciplineFilter)) {
			return false;
		}

		return true;
	});
}

export function NewInterventionSearch() {
	const { wizardState, goalAndInterventionDefinitionLibrary, existingInterventions, dispatch } = useGoalsAndInterventionsWizard();
	const [searchTerm, setSearchTerm] = useState('');
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(3);
	const [categoriesExpanded, setCategoriesExpanded] = useState(false);
	const { data: categories } = useSuspenseQuery(getCategoriesOptions());
	const getCategoryLabel = useGetCategoryLabel();

	const interventionCategoryNames = new Set([...goalAndInterventionDefinitionLibrary.interventionDefinitions.map(x => x.category)]);
	const interventionCategories = categories.filter(x => interventionCategoryNames.has(x.name));

	const existingInterventionDefinitionIds = existingInterventions
		.filter(x => x.interventionDefinitionTypeId === InterventionDefinitionTypeEnum.Predefined)
		.map(x => x.interventionDefinitionId)
		.filter(x => x !== null) as string[];

	const filteredInterventions = filterInterventions(
		goalAndInterventionDefinitionLibrary!,
		existingInterventionDefinitionIds,
		wizardState.selectedInterventions,
		wizardState.selectedInterventionCategories,
		wizardState.selectedInterventionDiscipline,
		searchTerm
	);

	const sortedInterventions = filteredInterventions.toSorted((x, y) => x.title.toLocaleLowerCase().localeCompare(y.title.toLocaleLowerCase()));
	const totalPages = Math.max(1, Math.ceil(sortedInterventions.length / pageSize));
	const page = Math.min(currentPage, totalPages);
	const pagedInterventions = sortedInterventions.slice((page - 1) * pageSize, page * pageSize);

	return (
		<>
			<Section>
				<div className="flex flex-col gap-4">
					<SearchInput value={searchTerm} onChange={setSearchTerm} placeholder="Search interventions..." />
					{interventionCategories.length > 0 && (
						<div className="flex items-start gap-2">
							<div className={clsx('flex-1', !categoriesExpanded && 'max-h-8 overflow-hidden')}>
								<ButtonGroupField
									options={interventionCategories.map(x => ({ id: x.name, label: getCategoryLabel(x.name) }))}
									isSelected={(id: string) => wizardState.selectedInterventionCategories.includes(id)}
									onSelect={(id: string) => dispatch({ type: 'INTERVENTION_CATEGORY_TOGGLED', category: id })}
									label="Category"
									hideLabel
									variant="chips"
								/>
							</div>
							<Button
								appearance="ghost"
								intent="neutral"
								size="xs"
								label={categoriesExpanded ? 'Less' : 'More'}
								Icon={categoriesExpanded ? ChevronUp : ChevronDown}
								onClick={() => setCategoriesExpanded(x => !x)}
							/>
						</div>
					)}
				</div>
			</Section>
			{searchTerm.length > 3 && filteredInterventions.length === 0 ? (
				<div className="text-center text-sm text-gray-500">No interventions found</div>
			) : (
				<>
					{sortedInterventions.length > 0 && (
						<DataTablePager
							currentPage={page}
							totalPages={totalPages}
							totalCount={sortedInterventions.length}
							pageSize={pageSize}
							pageSizeOptions={[3, 5, 10]}
							onPageChange={setCurrentPage}
							onPageSizeChange={size => {
								setPageSize(size);
								setCurrentPage(1);
							}}
						/>
					)}
					{pagedInterventions.map(x => (
						<InterventionCard
							key={x.id}
							interventionDefinition={x}
							selection={{
								selected: false,
								onSelect: () => {
									dispatch({
										type: 'PREDEFINED_INTERVENTION_SELECTED',
										interventionDefinition: x,
										newInterventionId: window.crypto.randomUUID()
									});
								}
							}}
						/>
					))}
				</>
			)}
		</>
	);
}
